import React, { useContext, useCallback, useEffect, useState } from 'react';
import StudyHubBackNav from '../navigation/StudyHubBackNav';
import Navbar from '../Items/Navbar';
import AuthContext from '../shared/AuthContext';
import LearningFlashcard from '../learning/LearningFlashcard';
import LearningLiveBar from '../learning/LearningLiveBar';
import { getDailyReviewGoal, getPracticeSource } from '../../utils/learningPreferences';
import { normalizeLearningDeck } from '../../utils/normalizeLearningQuestion';
import useLiveLearningStats from '../../hooks/useLiveLearningStats';
import { showGamificationToast } from '../gamification/GamificationToast';
import { invalidateRealtime } from '../../realtime/realtimeBus';
import GamificationQuickBar from '../gamification/GamificationQuickBar';
import LearningGuide from '../learning/LearningGuide';
import NavShortcutGrid, { NavShortcut } from '../navigation/NavShortcutGrid';
import '../navigation/appNav.css';
import '../learning/learning-page-ui.css';
import '../learning/LearningFlashcard.css';
import '../learning/LearningLiveBar.css';

const PracticePage = () => {
  const { apiClient, user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState('');
  const dailyGoal = getDailyReviewGoal();
  const {
    dueCount,
    savedCount,
    reviewedToday,
    updatedAt,
    refreshing,
    refresh: refreshLiveStats,
  } = useLiveLearningStats();
  const userProfile = user || JSON.parse(localStorage.getItem('userProfile') || 'null');
  const current = items[index];

  const loadDeck = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const source = getPracticeSource();
      const response = await apiClient.get('/api/learning/practice', {
        params: { source },
        timeout: 30000,
      });
      const deck = normalizeLearningDeck(response.data);
      setItems(deck);
      setIndex(0);
      setRevealed(false);
      if (deck.length === 0) {
        setMessage('No questions in your practice deck. Save from the Library or switch to library mode in Settings.');
      } else {
        setMessage(`${deck.length} questions loaded (${source === 'library' ? 'library sample' : 'saved'}).`);
      }
      refreshLiveStats(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load practice deck.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, refreshLiveStats]);

  useEffect(() => {
    loadDeck();
  }, [loadDeck]);

  useEffect(() => {
    const onPrefs = () => loadDeck();
    window.addEventListener('qnaread:learning-prefs', onPrefs);
    return () => window.removeEventListener('qnaread:learning-prefs', onPrefs);
  }, [loadDeck]);

  const advance = () => {
    setRevealed(false);
    setIndex((prev) => prev + 1);
  };

  const grade = async (value) => {
    if (!current) return;
    setGrading(value);
    try {
      const res = await apiClient.post(`/api/learning/review/${current.id}`, { grade: value, context: 'practice' });
      showGamificationToast(res.data?.gamification);
      invalidateRealtime('practice-grade');
      setMessage(`Graded ${value.toLowerCase()}. Schedule updated.`);
      refreshLiveStats(true);
      advance();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setGrading('');
    }
  };

  const sessionLabel = items.length
    ? `Card ${Math.min(index + 1, items.length)} of ${items.length}`
    : '';

  const showSkeleton = loading && items.length === 0;

  return (
    <div className="learning-page">
      <Navbar userProfile={userProfile} loading={loading && items.length === 0} />
      <main className="learning-page-shell">
        <StudyHubBackNav />
        <h1 className="learning-page-title">Practice</h1>
        <GamificationQuickBar />
        <LearningGuide focus="practice" />
        <LearningLiveBar
          dueCount={dueCount}
          savedCount={savedCount}
          reviewedToday={reviewedToday}
          dailyGoal={dailyGoal}
          updatedAt={updatedAt}
          refreshing={refreshing}
          onRefresh={refreshLiveStats}
        />
        <p className="learning-page-lead">
          Reveal the answer, then grade yourself. Each grade updates when that question will appear on Review.
        </p>
        {error && <p role="alert" className="learning-alert">{error}</p>}
        {message && !error && <p className="learning-status">{message}</p>}
        {showSkeleton && (
          <div className="learning-card-skeleton" aria-busy="true" aria-label="Loading questions">
            <p />
            <p />
            <p />
          </div>
        )}
        {!loading && index >= items.length && items.length > 0 && (
          <div className="learning-empty">
            <p>Session complete.</p>
            <NavShortcutGrid>
              <NavShortcut to="/review" label="Review queue" description="Grade what is due now" variant="primary" />
              <button type="button" className="nav-shortcut nav-shortcut--secondary" onClick={loadDeck} style={{ cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                <span className="nav-shortcut-label">Another round</span>
                <span className="nav-shortcut-desc">Reload the same deck</span>
              </button>
            </NavShortcutGrid>
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="learning-empty">
            <p>Your practice deck is empty. Save questions from the library or switch to library mode in Settings.</p>
            <NavShortcutGrid>
              <NavShortcut to="/library" label="Library" description="Browse & save questions" variant="primary" />
              <NavShortcut to="/settings" label="Settings" description="Library practice mode" />
              <button type="button" className="nav-shortcut nav-shortcut--secondary" onClick={loadDeck} style={{ cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                <span className="nav-shortcut-label">Reload deck</span>
                <span className="nav-shortcut-desc">Try fetching again</span>
              </button>
            </NavShortcutGrid>
          </div>
        )}
        {!showSkeleton && current && index < items.length && (
          <LearningFlashcard
            item={current}
            revealed={revealed}
            onReveal={() => setRevealed(true)}
            onGrade={grade}
            grading={grading}
            sessionLabel={sessionLabel}
            onSkip={advance}
          />
        )}
      </main>
    </div>
  );
};

export default PracticePage;
